import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core'
import { ISelectItem } from '@lla-platform/core/core-ui'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { PillSize } from '../pill/pill.component'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { MatSelect } from '@angular/material/select'
import { MobileMaxWidth } from '@lla-platform/core/core-util'

export interface IDisableItem {
  value: any
  reason?: string
}

@UntilDestroy()
@Component({
  selector: 'lla-items-selector-filter',
  templateUrl: './items-selector-filter.component.html',
  styleUrls: ['./items-selector-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ItemsSelectorFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() panelWidth: 'auto' | number | null = 'auto'
  @Input() fullWidthPanel = false
  @Input() selectMenuInputId: string
  @Input() searchable = true
  @Input() searchPlaceholder = 'search'
  @Input() inlineSearch = false
  @Input() icon = 'garage'
  @Input() label = 'Select location'
  @Input() prefix?: string
  @Input() capitalizeLabel = true
  @Input() allItems: ISelectItem[] = []
  @Input() disableItems: IDisableItem[] = []
  @Input() selectedId: any
  @Input() selectedIds: any[] = []
  @Input() pillSize: PillSize = 'm'
  @Input() extraClasses = ''
  @Input() selectorClass = ''
  @Input() multiSelect = false
  @Input() multiSelectLabel = 'items'
  @Input() persistSelect = true
  @Input() disabled = false
  @Input() showHeader = true
  @Output() selectedIdChanged = new EventEmitter<any>()

  @ViewChild('selectorRef') selectorRef: MatSelect
  selectedItems: ISelectItem[] = []
  resolvedPanelWidth: 'auto' | number | null = 'auto'

  form: UntypedFormGroup
  isOpen = false
  selectedDisplayName = ''
  window = window
  mobileMaxWidth = MobileMaxWidth

  private panelKeydownHandler?: (event: KeyboardEvent) => void
  private currentPanelEl?: HTMLElement

  constructor(
    private fb: UntypedFormBuilder,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  @HostListener('window:scroll')
  onScroll() {
    if (!this.selectorRef?.panelOpen) return
    const panelEl = this.selectorRef.panel?.nativeElement as HTMLElement | undefined
    const active = document.activeElement as HTMLElement | null
    if (panelEl && active && panelEl.contains(active)) return
    this.selectorRef.close()
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      value: [this.multiSelect ? (this.selectedIds ?? []) : this.selectedId],
      searchText: [null]
    })

    this.form
      .get('searchText')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.filterItems()
      })

    this.form
      .get('value')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((value: any) => {
        if (this.multiSelect) {
          this.selectedIds = value || []
          this.selectedIdChanged.emit(this.selectedIds)
        } else {
          this.selectedIdChanged.emit(value)
        }
        if (!this.persistSelect) {
          this.form.get('value')?.setValue(undefined, {
            emitEvent: false
          })
        }
        this.updateDisplayName()
        this.focusOnInputId()
      })

    this.updateDisplayName()
  }

  public resetToDefault() {
    this.form.get('value')?.setValue(this.multiSelect ? (this.selectedIds ?? []) : this.selectedId, {
      emitEvent: false
    })
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    const selectedId = simpleChanges['selectedId']
    if (selectedId && !selectedId.firstChange && this.form) {
      this.form.get('value')?.setValue(selectedId?.currentValue, {
        emitEvent: false
      })
    }

    this.filterItems()
    this.updateDisplayName()
    this.resolvedPanelWidth = this.panelWidth
  }

  filterItems() {
    const value = this.form?.get('searchText')?.value
    const trimValue = value?.toLowerCase().trim() ?? ''
    if (trimValue === '') {
      this.selectedItems = [...this.allItems]
    } else {
      this.selectedItems = this.allItems.filter((el) => el.label.toLowerCase().includes(trimValue))
    }
    // After filtering, force-activate the first non-search option so ArrowDown / Enter
    // works immediately — ngx-mat-select-search's own auto-activate is unreliable when
    // narrowing filters (e.g. typing "Norr" leaves no active item even though one
    // option matches). Defer one tick so the @for has rendered the new mat-options.
    if (this.selectorRef?.panelOpen) {
      setTimeout(() => this.activateFirstFilteredOption())
    }
  }

  private activateFirstFilteredOption(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const keyManager = (this.selectorRef as any)?._keyManager as
      { setActiveItem: (i: number) => void; activeItem: unknown } | undefined
    if (!keyManager) return
    const options = this.selectorRef.options?.toArray() ?? []
    const firstEnabledIndex = options.findIndex((opt) => !opt.disabled)
    if (firstEnabledIndex < 0) return
    const activeItem = keyManager.activeItem as
      { disabled?: boolean; _active?: boolean } | null | undefined
    const activeIsValid =
      !!activeItem &&
      !activeItem.disabled &&
      activeItem._active === true &&
      options.some((o) => (o as unknown) === activeItem)
    if (!activeIsValid) {
      keyManager.setActiveItem(firstEnabledIndex)
    }
  }

  getDisableItem(value: any) {
    return this.disableItems?.find((el) => el.value === value)
  }

  selectedItemName() {
    if (this.multiSelect) {
      const selectedIds = this.form?.get('value')?.value || []
      if (selectedIds.length === 0) {
        return ''
      } else if (selectedIds.length === 1) {
        return this.allItems.find((el) => el.value === selectedIds[0])?.label
      } else {
        return `${selectedIds.length} ${this.multiSelectLabel} selected`
      }
    }
    return this.allItems.find((el) => el.value === this.selectedId)?.label
  }

  private updateDisplayName(): void {
    if (this.multiSelect) {
      const selectedIds = this.form?.get('value')?.value || []
      if (selectedIds.length === 0) {
        this.selectedDisplayName = ''
      } else if (selectedIds.length === 1) {
        this.selectedDisplayName = this.allItems.find((el) => el.value === selectedIds[0])?.label ?? ''
      } else {
        this.selectedDisplayName = `${selectedIds.length} ${this.multiSelectLabel} selected`
      }
    } else {
      this.selectedDisplayName = this.allItems.find((el) => el.value === this.selectedId)?.label ?? ''
    }
  }

  focusOnInputId() {
    const inputElement = document.getElementById(this.selectMenuInputId)
    if (inputElement) {
      inputElement?.focus()
    }
  }

  searchOnPress(event: KeyboardEvent) {
    if (this.inlineSearch && event?.key) {
      const foundItems = this.allItems
        .filter((el) => el.label.toLowerCase().startsWith(event.key))
        .map((el) => el.value)

      if (foundItems.length > 0) {
        let valueIndex = 0
        const currentIndex = foundItems.findIndex((el) => el === this.form.get('value')?.value)

        if (currentIndex !== foundItems.length - 1 && currentIndex + 1 < foundItems.length) {
          valueIndex = currentIndex + 1
        }
        this.form.get('value')?.setValue(foundItems[valueIndex])
      }
    }
  }

  openSelector() {
    if (this.disabled || this.selectorRef?.panelOpen) {
      return
    }
    if (this.fullWidthPanel) {
      this.resolvedPanelWidth = this.elementRef.nativeElement.offsetWidth || this.panelWidth
    }
    this.isOpen = true
    requestAnimationFrame(() => {
      this.selectorRef?.open()
    })
  }

  isSelectedLastItem(value: any): boolean {
    if (!this.multiSelect) return false

    const selectedValues = this.form.get('value')?.value || []
    return selectedValues.length === 1 && selectedValues.includes(value)
  }

  isSelected(value: any): boolean {
    const selectedValues = this.form.get('value')?.value || []
    return selectedValues.includes(value)
  }

  onOpenSelector(): void {
    // The panel template is rendered via cdkConnectedOverlay; defer one tick so
    // selectorRef.panel.nativeElement is populated before we attach the listener.
    setTimeout(() => this.attachPanelKeydownGuard())
  }

  onCloseSelector() {
    setTimeout(() => {
      this.isOpen = false
    }, 100)
    this.detachPanelKeydownGuard()
    this.focusOnInputId()
  }

  ngOnDestroy(): void {
    this.detachPanelKeydownGuard()
  }

  // Angular Material 21+'s mat-select binds `_handleKeydown` BOTH on the panel template
  // and via host listener on the mat-select host. With the popover overlay variant the
  // panel is rendered as a descendant of the host, so a single keydown bubbles through
  // both listeners — advancing the key manager twice (skipping every other option when
  // the user navigates via arrow keys, especially noticeable while filtering with
  // ngx-mat-select-search). Stopping propagation on the panel after Material's panel
  // handler has run prevents the duplicate host-level call.
  private attachPanelKeydownGuard(): void {
    const panelEl = this.selectorRef?.panel?.nativeElement as HTMLElement | undefined
    if (!panelEl) return
    this.detachPanelKeydownGuard()
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.stopPropagation()
      }
    }
    panelEl.addEventListener('keydown', handler)
    this.panelKeydownHandler = handler
    this.currentPanelEl = panelEl
  }

  private detachPanelKeydownGuard(): void {
    if (this.currentPanelEl && this.panelKeydownHandler) {
      this.currentPanelEl.removeEventListener('keydown', this.panelKeydownHandler)
    }
    this.panelKeydownHandler = undefined
    this.currentPanelEl = undefined
  }
}
